import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Plus } from "lucide-react";
import { useStore, CATEGORIES } from "@/context/StoreContext";
import { waProduct } from "@/lib/whatsapp";
import { formatGs } from "@/lib/format";

export default function Catalog() {
  const { products, loadingProducts, addToCart } = useStore();
  const [active, setActive] = useState("todos");

  const filtered = active === "todos" ? products : products.filter((p) => p.category === active);

  return (
    <section id="catalogo" className="py-20 lg:py-28 bg-cream border-b-2 border-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
          <span className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-kpink">El Catálogo</span>
          <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-2">
            Todo lo lindo, en un solo lugar
          </h2>
          <p className="text-ink/60 mt-3 max-w-xl font-medium">
            Tocá “Agregar” para armar tu pedido o consultanos directo por WhatsApp por cualquier producto.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2.5 mt-8" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`category-filter-${c.id}`}
              className={`px-5 py-2.5 rounded-full border-2 border-ink font-bold text-sm transition-all duration-200 ${
                active === c.id ? `${c.color} shadow-neo -translate-y-0.5 ${c.id === "agendas" || c.id === "regalos" ? "text-white" : ""}` : "bg-white hover:-translate-y-0.5"
              }`}>
              {c.label}
            </button>
          ))}
        </div>

        {loadingProducts ? (
          <div className="py-24 text-center font-display font-bold text-xl text-ink/50" data-testid="catalog-loading">
            Cargando productos...
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10" data-testid="product-grid">
            <AnimatePresence mode="popLayout">
              {filtered.map((p) => (
                <motion.article key={p.id} layout
                  initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  data-testid={`product-card-${p.id}`}
                  className="group bg-white border-2 border-ink rounded-3xl overflow-hidden shadow-neo hover:shadow-neo-lg hover:-translate-y-1.5 transition-all duration-300 flex flex-col">
                  <div className="relative overflow-hidden border-b-2 border-ink">
                    <img src={p.image} alt={p.name} loading="lazy"
                      className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500" />
                    {p.tag && (
                      <span className="absolute top-3 left-3 bg-kyellow border-2 border-ink rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider -rotate-2 shadow-neo-sm">
                        {p.tag}
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-base leading-snug flex-1">{p.name}</h3>
                    <div className="flex items-center justify-between mt-3">
                      <span className="font-display font-extrabold text-2xl">{formatGs(p.price)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <button onClick={() => addToCart(p)} data-testid={`product-add-order-btn-${p.id}`}
                        className="h-11 inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-ink bg-kyellow font-bold text-sm shadow-neo-sm hover:-translate-y-0.5 active:translate-y-0 transition-transform duration-150">
                        <Plus size={16} strokeWidth={3} /> Agregar
                      </button>
                      <a href={waProduct(p)} target="_blank" rel="noopener noreferrer" data-testid={`product-whatsapp-btn-${p.id}`}
                        className="h-11 inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-ink bg-[#25D366] text-white font-bold text-sm shadow-neo-sm hover:-translate-y-0.5 active:translate-y-0 transition-transform duration-150">
                        <MessageCircle size={16} strokeWidth={2.5} /> Pedir
                      </a>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
}
